import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Typography, Space, theme, Badge, Modal, Form, Input, message, Descriptions, Tag } from 'antd';
import {
  TeamOutlined, BarChartOutlined, DashboardOutlined,
  LogoutOutlined, UserOutlined, MenuFoldOutlined, MenuUnfoldOutlined,
  SettingOutlined, FileTextOutlined, LockOutlined,
} from '@ant-design/icons';
import { agentAPI } from '../services/api';
import dayjs from 'dayjs';

const { Header, Sider, Content } = Layout;
const { Text, Title } = Typography;

const menuItems = [
  { key: '/dashboard', icon: <DashboardOutlined />, label: '数据概览' },
  { key: '/users', icon: <TeamOutlined />, label: '用户管理' },
  { key: '/stats', icon: <BarChartOutlined />, label: '消费统计' },
  { key: '/billing', icon: <FileTextOutlined />, label: '流水明细' },
];

export default function MainLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [agentInfo, setAgentInfo] = useState(JSON.parse(localStorage.getItem('agent_info') || '{}'));
  const [pwdModalOpen, setPwdModalOpen] = useState(false);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [pwdForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    loadAgentInfo();
  }, []);

  const loadAgentInfo = async () => {
    try {
      const res = await agentAPI.getInfo();
      setAgentInfo(res.data);
      localStorage.setItem('agent_info', JSON.stringify(res.data));
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('agent_token');
        localStorage.removeItem('agent_info');
        navigate('/login');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('agent_token');
    localStorage.removeItem('agent_info');
    navigate('/login');
  };

  const openPwdModal = () => {
    pwdForm.resetFields();
    setPwdModalOpen(true);
  };

  const handleChangePassword = async () => {
    try {
      const values = await pwdForm.validateFields();
      setPwdLoading(true);
      await agentAPI.changePassword(values.old_password, values.new_password);
      message.success('密码修改成功，请重新登录');
      setPwdModalOpen(false);
      // 密码已变，安全起见强制重新登录
      setTimeout(() => {
        localStorage.removeItem('agent_token');
        localStorage.removeItem('agent_info');
        navigate('/login');
      }, 800);
    } catch (err) {
      if (err?.errorFields) return; // 表单校验
      message.error(err?.response?.data?.message || '修改失败，请检查当前密码');
    } finally {
      setPwdLoading(false);
    }
  };

  const userMenu = {
    items: [
      { key: 'profile', icon: <UserOutlined />, label: '账号信息', onClick: () => setInfoModalOpen(true) },
      { key: 'changePwd', icon: <LockOutlined />, label: '修改密码', onClick: openPwdModal },
      { type: 'divider' },
      { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', onClick: handleLogout, danger: true },
    ],
  };

  const getBreadcrumb = () => {
    const item = menuItems.find(m => m.key === location.pathname);
    return item ? item.label : '控制台';
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 暗色侧边栏 - 固定，不随内容滚动 */}
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={240}
        style={{
          background: 'linear-gradient(180deg, #0f1419 0%, #1a2332 100%)',
          borderRight: '1px solid rgba(255,255,255,0.06)',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          height: '100vh',
          overflow: 'auto',
          zIndex: 20,
        }}
      >
        {/* Logo区域 */}
        <div style={{
          height: 72,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? 0 : '0 24px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, overflow: 'hidden',
          }}>
            <img src="/logo.png" alt="灵狐算力" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          {!collapsed && (
            <div style={{ marginLeft: 12, lineHeight: 1.3 }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{agentInfo.agent_name || '灵狐算力'}</div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>企业控制台</div>
            </div>
          )}
        </div>

        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[location.pathname]}
          defaultSelectedKeys={['/dashboard']}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{
            background: 'transparent',
            borderRight: 'none',
            marginTop: 12,
            padding: '0 8px',
          }}
        />

        {/* 底部信息 */}
        {!collapsed && (
          <div style={{
            position: 'absolute', bottom: 0, left: 0, right: 0,
            padding: '16px 20px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
          }}>
            <Text style={{ color: 'rgba(255,255,255,0.35)', fontSize: 12 }}>
              灵狐算力 · 企业管理平台
            </Text>
          </div>
        )}
      </Sider>

      {/* 主区域 - 左侧留出固定侧边栏宽度 */}
      <Layout style={{ marginLeft: collapsed ? 80 : 240, transition: 'margin-left 0.2s' }}>
        {/* 顶部导航栏 - 固定吸顶，不随内容滚动 */}
        <Header style={{
          background: '#fff',
          padding: '0 28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          height: 64,
        }}>
          <Space size="middle">
            {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
              onClick: () => setCollapsed(!collapsed),
              style: { fontSize: 18, cursor: 'pointer', color: '#666' },
            })}
            <div style={{ width: 1, height: 24, background: '#f0f0f0' }} />
            <Title level={5} style={{ margin: 0, fontWeight: 600 }}>{getBreadcrumb()}</Title>
          </Space>

          <Dropdown menu={userMenu} placement="bottomRight" trigger={['click']}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, cursor: 'pointer', padding: '4px 12px', borderRadius: 8, transition: 'background 0.2s' }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <Avatar
                icon={<UserOutlined />}
                style={{ background: 'linear-gradient(135deg, #667eea, #764ba2)' }}
                size={32}
              />
              <div style={{ lineHeight: 1.15 }}>
                <div style={{ fontWeight: 600, fontSize: 12 }}>{agentInfo.agent_name || '代理商'}</div>
                <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>管理员</Text>
              </div>
            </div>
          </Dropdown>
        </Header>

        {/* 内容区域 - 唯一滚动区 */}
        <Content style={{
          margin: '20px 24px',
          minHeight: 280,
        }}>
          <Outlet context={{ agentInfo }} />
        </Content>
      </Layout>

      {/* 账号信息弹窗 */}
      <Modal
        title="账号信息"
        open={infoModalOpen}
        onCancel={() => setInfoModalOpen(false)}
        footer={null}
        width={520}
      >
        <Descriptions column={1} bordered size="small" style={{ marginTop: 12 }}
          labelStyle={{ width: 120, background: '#fafafa' }}>
          <Descriptions.Item label="代理商名称">{agentInfo.agent_name || '-'}</Descriptions.Item>
          <Descriptions.Item label="代理商码">{agentInfo.agent_code || '-'}</Descriptions.Item>
          <Descriptions.Item label="OEM 站点">
            {agentInfo.subdomain
              ? <Typography.Text copyable>{`${agentInfo.subdomain}.linghusuanli.com`}</Typography.Text>
              : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="登录账号">{agentInfo.owner_email || '-'}</Descriptions.Item>
          <Descriptions.Item label="站点标题">{agentInfo.site_title || '-'}</Descriptions.Item>
          <Descriptions.Item label="状态">
            {agentInfo.status === 'active'
              ? <Tag color="green">正常</Tag>
              : <Tag color="red">已停用</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="本站用户数">
            <Tag color="blue">{agentInfo.user_count || 0}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="联系人">{agentInfo.contact_name || '-'}</Descriptions.Item>
          <Descriptions.Item label="联系电话">{agentInfo.contact_phone || '-'}</Descriptions.Item>
          <Descriptions.Item label="联系邮箱">{agentInfo.contact_email || '-'}</Descriptions.Item>
          <Descriptions.Item label="开通时间">
            {agentInfo.created_at ? dayjs(agentInfo.created_at).format('YYYY-MM-DD HH:mm') : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Modal>

      {/* 修改密码弹窗 */}
      <Modal
        title="修改密码"
        open={pwdModalOpen}
        onOk={handleChangePassword}
        onCancel={() => setPwdModalOpen(false)}
        confirmLoading={pwdLoading}
        okText="确认修改"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={pwdForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="old_password" label="当前密码"
            rules={[{ required: true, message: '请输入当前密码' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="当前登录密码" />
          </Form.Item>
          <Form.Item name="new_password" label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 8, message: '新密码至少 8 位' },
            ]}>
            <Input.Password prefix={<LockOutlined />} placeholder="至少 8 位" />
          </Form.Item>
          <Form.Item name="confirm_password" label="确认新密码" dependencies={['new_password']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) return Promise.resolve();
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}>
            <Input.Password prefix={<LockOutlined />} placeholder="再次输入新密码" />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            提示：修改后当前登录会失效，需用新密码重新登录。
          </Text>
        </Form>
      </Modal>
    </Layout>
  );
}
